import { useState } from 'react';
import type { CarpetPricingCatalogItem } from '@jobos/shared';
import { StepperInput } from './StepperInput';

interface RowState {
  qty: number;
  discount: number;
}

interface CategoryCardProps {
  title: string;
  items: CarpetPricingCatalogItem[];
  rows: Record<string, RowState>;
  onQtyChange: (key: string, qty: number) => void;
  onDiscountChange: (key: string, discount: number) => void;
  hideEmpty?: boolean;
  readOnly?: boolean;
}

const formatMoney = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

export function CategoryCard({
  title,
  items,
  rows,
  onQtyChange,
  onDiscountChange,
  hideEmpty,
  readOnly = false,
}: CategoryCardProps) {
  const [openDiscountKey, setOpenDiscountKey] = useState<string | null>(null);

  // In read-only mode, hide items that weren't selected to reduce noise.
  const visible = readOnly || hideEmpty
    ? items.filter((it) => (rows[it.key]?.qty ?? 0) > 0)
    : items;
  const nonZeroCount = items.reduce(
    (sum, it) => sum + ((rows[it.key]?.qty ?? 0) > 0 ? 1 : 0),
    0,
  );

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {nonZeroCount > 0 && (
          <span className="text-xs text-gray-500">{nonZeroCount} selected</span>
        )}
      </div>
      <div className="divide-y divide-gray-50">
        {visible.length === 0 && (
          <div className="px-4 py-3 text-sm text-gray-400 italic">No items.</div>
        )}
        {visible.map((item) => {
          const row = rows[item.key] ?? { qty: 0, discount: 0 };
          const rate = Number(item.defaultRate);
          const total = rate * row.qty - (row.discount ?? 0);
          const discountOpen = openDiscountKey === item.key;
          return (
            <div key={item.key} className="px-4 py-2">
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="truncate text-sm text-gray-900">{item.label}</div>
                  <div className="text-xs text-gray-500">{formatMoney(rate)} each</div>
                </div>
                <StepperInput
                  value={row.qty}
                  ariaLabel={`${item.label} quantity`}
                  onChange={(next) => onQtyChange(item.key, next)}
                  readOnly={readOnly}
                />
                <div className="w-20 text-right text-sm tabular-nums text-gray-700">
                  {row.qty > 0 ? formatMoney(total) : ''}
                </div>
                {!readOnly && (
                  <button
                    type="button"
                    aria-label={`${item.label} more options`}
                    className="text-gray-400 hover:text-gray-600 px-1"
                    onClick={() =>
                      setOpenDiscountKey(discountOpen ? null : item.key)
                    }
                  >
                    …
                  </button>
                )}
              </div>
              {discountOpen && (
                <div className="mt-2 flex items-center gap-2">
                  <label
                    htmlFor={`discount-${item.key}`}
                    className="text-xs text-gray-500"
                  >
                    Discount
                  </label>
                  <input
                    id={`discount-${item.key}`}
                    type="number"
                    min={0}
                    step="0.01"
                    value={row.discount || ''}
                    placeholder="0"
                    className="w-24 rounded border border-gray-200 px-2 py-1 text-right text-sm"
                    onChange={(e) => {
                      const n = Number(e.target.value);
                      onDiscountChange(item.key, Number.isFinite(n) ? n : 0);
                    }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
